import React from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_CONFIG = {
  trending:    { label: 'Trending',          bg: 'bg-red-500',    emoji: '\uD83D\uDD25' },
  fast:        { label: 'Fast Results',      bg: 'bg-amber-500',  emoji: '\u26A1' },
  downloaded:  { label: 'Most Downloaded',   bg: 'bg-blue-500',   emoji: '\u2B50' },
  beginner:    { label: 'Beginner Friendly', bg: 'bg-emerald-500', emoji: '\uD83E\uDDE0' },
  value:       { label: 'Best Value',        bg: 'bg-orange-500', emoji: '\uD83D\uDCB0' },
};

export const BookMockup = ({ productId, title, size = 'md', className = '', badge }) => {
  const sizeConfig = {
    sm: { wrapper: 'h-40', book: 'w-24 h-32' },
    md: { wrapper: 'h-52', book: 'w-32 h-44' },
    lg: { wrapper: 'h-64', book: 'w-40 h-56' },
    xl: { wrapper: 'h-80', book: 'w-52 h-72' },
    card: { wrapper: 'h-48', book: 'w-28 h-40' },
  };

  const cfg = sizeConfig[size] || sizeConfig.md;
  const badgeCfg = badge ? BADGE_CONFIG[badge] : null;

  return (
    <div
      className={`${cfg.wrapper} bg-white flex items-center justify-center overflow-hidden relative ${className}`}
      data-testid={`book-mockup-${productId}`}
      style={{ perspective: '800px' }}
    >
      {/* Badge overlay — top-left */}
      {badgeCfg && (
        <div
          className={`absolute top-2 left-2 z-20 ${badgeCfg.bg} text-white text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded-md shadow-md flex items-center gap-0.5 leading-tight`}
          data-testid={`badge-${badge}-${productId}`}
        >
          <span>{badgeCfg.emoji}</span>
          <span>{badgeCfg.label}</span>
        </div>
      )}

      <div className="book-mockup-inner" style={{
        transformStyle: 'preserve-3d',
        transform: 'rotateY(-18deg) rotateX(2deg)',
        position: 'relative',
      }}>
        {/* Front cover */}
        <div className={`${cfg.book} relative rounded-r-sm overflow-hidden`} style={{
          boxShadow: '6px 6px 18px rgba(0,0,0,0.25), 2px 2px 6px rgba(0,0,0,0.15)',
          transformStyle: 'preserve-3d',
        }}>
          <img
            src={`${API}/cover/${productId}`}
            alt={title || 'Guide cover'}
            className="w-full h-full object-cover"
            style={{ backfaceVisibility: 'hidden' }}
          />
          {/* Spine edge - left side highlight */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            background: 'linear-gradient(to right, rgba(0,0,0,0.3), rgba(0,0,0,0.05))',
          }} />
          {/* Top edge - subtle light */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '2px',
            background: 'rgba(255,255,255,0.3)',
          }} />
        </div>

        {/* Spine (pages) */}
        <div style={{
          position: 'absolute',
          top: '2px',
          left: '-10px',
          width: '10px',
          height: 'calc(100% - 4px)',
          background: 'linear-gradient(to right, #e8e4df, #f5f2ee, #ebe7e2)',
          transform: 'rotateY(90deg)',
          transformOrigin: 'right center',
          borderRadius: '1px 0 0 1px',
        }}>
          {/* Page lines */}
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              top: `${12 + i * 11}%`,
              left: '2px',
              right: '2px',
              height: '0.5px',
              background: 'rgba(0,0,0,0.06)',
            }} />
          ))}
        </div>

        {/* Bottom shadow */}
        <div style={{
          position: 'absolute',
          bottom: '-12px',
          left: '10%',
          width: '90%',
          height: '12px',
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.15) 0%, transparent 70%)',
          filter: 'blur(2px)',
        }} />
      </div>
    </div>
  );
};

// Deterministic badge assignment based on product ID
export const getBadge = (product) => {
  if (!product) return null;
  if (product.trending) return 'trending';
  // Use a simple hash of the product ID to assign consistently
  const id = product.id || '';
  const hash = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const badges = ['fast', 'downloaded', 'beginner', 'value'];
  return badges[hash % badges.length];
};

export default BookMockup;
